import restClient, { RestRequestOptions, RestResponse } from '.';
import { UserId, Uuid } from '../../models/Base';
import {
  AlbumSummaryJson,
  ChallengeRecordJson,
  GameRecordJson,
} from '../../models/RecordStore';
import {
  AlbumJson,
  LearningPeriod,
  SpaceJson,
  SpaceMembershipJson,
  SpaceMembershipTierInventoryJson,
  SpaceMembershipTierJson,
  SpaceSummaryJson,
  UserRankingJson,
} from '../../models/SpaceStore';
import { DefaultGameDesign } from '../../models/UserStore';
import {
  ModifiedPick,
  NonEmptyArray,
  Optional,
  PartialPick,
} from '../../models/Utils';
import {
  CursorData,
  CursorParams,
  Scope,
  ToAttachmentParams,
  UploadAttachmentData,
  WithProfilesData,
  WithProfilesParams,
} from '../utils';

export interface ClassroomData {
  group: SpaceJson;
  rootId: UserId;
  membership: SpaceMembershipJson | null;
  subscriptionVouchers?: unknown[];
  userMembership?: SpaceMembershipJson;
}

export interface SpacesData {
  classrooms: ClassroomData[];
}

export const getSpaces = <
  P extends {
    relatedTo?: string;
    precedent?: boolean;
    type?: string;
    userId?: UserId;
    state?: string | string[];
    role?: string | string[];
    isFavourite?: boolean;
    admin?: boolean;
  } & CursorParams &
    Optional<WithProfilesParams>,
>(
  params: P = {} as P,
  options?: RestRequestOptions,
): Promise<RestResponse<SpacesData & CursorData & WithProfilesData<P>>> => {
  return restClient.get(`/classrooms`, params, options);
};

interface GetSpaceData {
  classroom: {
    group: SpaceJson;
    rootId: UserId;
    membership: SpaceMembershipJson | null;
  };
}

export const getSpace = <P extends Optional<WithProfilesParams>>(
  path: { classroomId: Uuid },
  params: P = {} as P,
  options?: RestRequestOptions,
): Promise<RestResponse<GetSpaceData & WithProfilesData<P>>> => {
  return restClient.get(`/classrooms/${path.classroomId}`, params, options);
};

type GetTierInventoriesData = {
  classroomMembershipTierInventories: SpaceMembershipTierInventoryJson[];
  classroomMembershipTiers: SpaceMembershipTierJson[];
} & GetSpaceData;

export const getSpaceMembershipTierInventories = (
  params: CursorParams,
  options?: RestRequestOptions,
): Promise<RestResponse<GetTierInventoriesData & CursorData>> => {
  return restClient.get(
    `/classrooms/classroom-membership-tier-inventories`,
    params,
    options,
  );
};

export type ActivateSpaceMembershipTierInventoryBody = {
  subscriptionType: 'monthly' | 'permanent';
  email?: string;
  userId?: string;
};
type ActivateSpaceMembershipTierInventoryData = {
  // classroomMembershipTierSubscription: classroomMembershipTierSubscription
  classroomMembershipTierInventory: SpaceMembershipTierInventoryJson;
};
export const activateSpaceMembershipTierInventory = (
  path: {
    classroomId: string;
    tierId: string;
  },
  body: ActivateSpaceMembershipTierInventoryBody,
  options?: RestRequestOptions,
): Promise<RestResponse<ActivateSpaceMembershipTierInventoryData>> => {
  return restClient.post(
    `/classrooms/${path.classroomId}/classroom-membership-tiers/${path.tierId}/subscription`,
    body,
    options,
  );
};

// space membership
export interface MembershipData {
  membership: SpaceMembershipJson;
  password?: string;
}
export enum SpaceMembershipOperation {
  Visit = 'visit',
  Follow = 'follow',
  Join = 'join',
  CancelJoin = 'cancelJoin',
  Accept = 'accept',
  Reject = 'reject',
  Quit = 'quit',
  Unfollow = 'unfollow',
  BlockGroup = 'blockGroup',
  UnblockGroup = 'unblockGroup',
}
export const changeUserSpaceMembership = (
  path: { classroomId: Uuid },
  body: PartialPick<
    SpaceMembershipJson,
    | 'preferredWritingSystem'
    | 'preferredSpeakingAccent'
    | 'learningLocales'
    | 'lastAccess'
  > & {
    operation: SpaceMembershipOperation;
    email?: string;
    userCode?: string;
    // invitedBy?: UserId;
  },
  options?: RestRequestOptions,
): Promise<RestResponse<MembershipData>> => {
  return restClient.post(
    `/classrooms/${path.classroomId}/membership`,
    body,
    options,
  );
};

export enum ChangeSpaceMembershipResult {
  Success = 'success',
  Error = 'error',
  InvalidOperation = 'invalid membership operation',
}

export interface LeaderboardData {
  userRanking: UserRankingJson;
  password?: string;
}
export const joinTeamForLeaderboard = (
  path: { classroomId: Uuid; leaderboardId: Uuid },
  body: {
    teamId: Uuid;
    email?: string;
  },
  options?: RestRequestOptions,
): Promise<RestResponse<LeaderboardData>> => {
  return restClient.post(
    `/classrooms/${path.classroomId}/leaderboards/${path.leaderboardId}/team`,
    body,
    options,
  );
};

export interface AlbumsData {
  gameSets: AlbumJson[];
}

export const getAlbums = <
  P extends {
    // state?: string | string[];
    // partitionId?: string;
    // createdBy?: UserId;
    // startedBefore?: string;
    // endedAfter?: string;
    // leaderboardId?: string;
    // weeklyLeaderboard?: string;
  } & CursorParams,
>(
  path: { classroomId: string },
  params: P = {} as P,
  options?: RestRequestOptions,
): Promise<RestResponse<AlbumsData & CursorData>> => {
  return restClient.get(
    `/classrooms/${path.classroomId}/game-sets`,
    params,
    options,
  );
};

export interface RecentAlbumsData {
  gameSets: AlbumJson[];
  classrooms: ClassroomData[];
}

export const getRecentAlbums = <
  P extends { classroomId?: string } & CursorParams &
    Optional<WithProfilesParams>,
>(
  params: P = {} as P,
  options?: RestRequestOptions,
): Promise<
  RestResponse<RecentAlbumsData & CursorData & WithProfilesData<P>>
> => {
  return restClient.get(`/classrooms/game-sets/recent`, params, options);
};

interface AlbumData {
  gameSet: AlbumJson;
}
export const getAlbum = (
  path: { classroomId: Uuid; gameSetId: Uuid },
  params: Scope = {},
  options?: RestRequestOptions,
): Promise<RestResponse<AlbumData>> => {
  return restClient.get(
    `/classrooms/${path.classroomId}/game-sets/${path.gameSetId}`,
    params,
    options,
  );
};

interface CreateAlbumData {
  gameSet: AlbumJson;
  // post, channel, data
}

export const createAlbum = (
  path: { classroomId: string },
  body: { defaultGameDesign?: DefaultGameDesign } & ModifiedPick<
    AlbumJson,
    'i18nGameSetInfo',
    | 'startedAt'
    | 'endedAt'
    | 'state'
    | 'partitionId'
    | 'index'
    | 'icons'
    | 'tags'
    | 'audience'
    | 'viewOnlyAudience'
    | 'isCopyable'
    | 'content'
    | 'challenge'
    | 'revision'
    | 'leaderboardId'
    | 'tournament'
  >,
  options?: RestRequestOptions,
): Promise<RestResponse<CreateAlbumData>> => {
  return restClient.post(
    `/classrooms/${path.classroomId}/game-sets/`,
    body,
    options,
  );
};

interface GameRecordData {
  gameRecord: GameRecordJson;
}
export const syncGameRecords = (
  body: {
    gameRecords: NonEmptyArray<
      ModifiedPick<
        GameRecordJson,
        'classroomId' | 'gameSetId' | 'gameId',
        | 'gameCollectionId'
        | 'gameHash'
        | 'type'
        | 'slideshowId'
        | 'slideshowHash'
        | 'sessionStartedAt'
        | 'startedAt'
        | 'fee'
        | 'endedAt'
        | 'durationInMs'
        | 'result'
        | 'reward'
        | 'score'
        | 'stars'
        | 'defeatedOpponents'
        | 'evaluation'
      >
    >;
  },
  options?: RestRequestOptions,
): Promise<
  RestResponse<{ gameRecords: (GameRecordData | { errorMessage: string })[] }>
> => {
  return restClient.post('/classrooms/game-records', body, options);
};

interface GameRecordsData {
  gameRecords: GameRecordJson[];
}
export const getGameRecords = <
  P extends {
    playedBy?: UserId;
    gameSetId?: Uuid;
    gameId?: Uuid;
    type?: string;
    from?: string;
    to?: string;
    evaluation?: string;
    evaluated?: boolean;
  } & CursorParams &
    Optional<WithProfilesParams>,
>(
  path: { classroomId: Uuid },
  params: P = {} as P,
  options?: RestRequestOptions,
): Promise<
  RestResponse<GameRecordsData & CursorData & WithProfilesData<P>>
> => {
  return restClient.get(
    `/classrooms/${path.classroomId}/game-records`,
    params,
    options,
  );
};

export const createGameRecord = (
  path: { classroomId: Uuid },
  body: ModifiedPick<
    GameRecordJson,
    'gameSetId' | 'gameId' | 'fee',
    'gameCollectionId' | 'gameHash' | 'type' | 'sessionStartedAt'
  >,
  options?: RestRequestOptions,
): Promise<RestResponse<GameRecordData>> => {
  return restClient.post(
    `/classrooms/${path.classroomId}/game-records`,
    body,
    options,
  );
};

export const updateGameRecord = (
  path: { classroomId: Uuid; gameRecordId: Uuid },
  body: PartialPick<
    ToAttachmentParams<GameRecordJson>,
    | 'endedAt'
    | 'durationInMs'
    | 'result'
    | 'reward'
    | 'score'
    | 'stars'
    | 'defeatedOpponents'
    | 'evaluation'
  > = {},
  options?: RestRequestOptions,
): Promise<RestResponse<GameRecordData & UploadAttachmentData>> => {
  return restClient.put(
    `/classrooms/${path.classroomId}/game-records/${path.gameRecordId}`,
    body,
    options,
  );
};

export interface GameOpponentsData {
  opponents: (GameRecordJson | null)[];
}
export const getGameOpponents = <
  P extends {
    gameSetId: Uuid;
    gameId: Uuid;
    limit?: number;
    isAssisted?: boolean;
  } & Optional<WithProfilesParams>,
>(
  path: { classroomId: Uuid },
  params: P,
  options?: RestRequestOptions,
): Promise<RestResponse<GameOpponentsData & WithProfilesData<P>>> => {
  return restClient.get(
    `/classrooms/${path.classroomId}/game-opponents`,
    params,
    options,
  );
};

interface ChallengeRecordsData {
  challengeRecords: ChallengeRecordJson[];
}

export const getChallengeRecords = <
  P extends {
    gameSetId: Uuid;
    type?: string;
  } & CursorParams,
>(
  path: { classroomId: Uuid },
  params: P = {} as P,
  options?: RestRequestOptions,
): Promise<RestResponse<ChallengeRecordsData & CursorData>> => {
  return restClient.get(
    `/classrooms/${path.classroomId}/challenge-records`,
    params,
    options,
  );
};

interface AlbumSummariesData {
  gameSetSummaries: AlbumSummaryJson[];
}
export const getAlbumSummaries = <
  P extends {
    gameSetIds?: Uuid[];
    classroomId?: Uuid;
  } & CursorParams,
>(
  params: P = {} as P,
  options?: RestRequestOptions,
): Promise<RestResponse<AlbumSummariesData & CursorData>> => {
  return restClient.get(`/classrooms/game-set-summaries`, params, options);
};

interface UpdateAlbumSummaryData {
  gameSetSummary: AlbumSummaryJson;
  classroomSummary: SpaceSummaryJson;
}
export const updateAlbumSummary = (
  path: { classroomId: Uuid; gameSetId: Uuid },
  options?: RestRequestOptions,
): Promise<RestResponse<UpdateAlbumSummaryData>> => {
  return restClient.post(
    `/classrooms/${path.classroomId}/game-sets/${path.gameSetId}/game-set-summaries`,
    {},
    options,
  );
};

interface AlbumSummaryData {
  gameSetSummary: AlbumSummaryJson;
}
export const moveInAlbumSummaryPath = (
  path: { classroomId: Uuid; gameSetId: Uuid },
  body: { moveTo: number },
  options?: RestRequestOptions,
): Promise<RestResponse<AlbumSummaryData>> => {
  return restClient.post(
    `/classrooms/${path.classroomId}/game-sets/${path.gameSetId}/path/move`,
    body,
    options,
  );
};

export interface SpaceSummaryData {
  classroomSummaries: SpaceSummaryJson[];
}

export const getSpaceSummaries = <
  P extends {
    classroomIds: Uuid[];
  },
>(
  params: P = {} as P,
  options?: RestRequestOptions,
): Promise<RestResponse<SpaceSummaryData>> => {
  return restClient.get(`/classrooms/classroom-summaries`, params, options);
};

interface UpdateSpaceSummaryData {
  classroomSummary: SpaceSummaryJson;
}
export const updateSpaceSummary = (
  path: { classroomId: Uuid },
  body: { currentLearningPeriod?: LearningPeriod<number> } = {},
  options?: RestRequestOptions,
): Promise<RestResponse<UpdateSpaceSummaryData>> => {
  return restClient.post(
    `/classrooms/${path.classroomId}/classroom-summaries`,
    body,
    options,
  );
};
